import React, { useState, useEffect, useRef, useCallback } from 'react'
import { SubDropdown, SiteNavButton, DropdownChevron, DesktopNavItem } from './styles'
import { Lk } from '../../../Utils/routing'
import type { NavMenuOption } from '../../../Config/League/league.config'

type SubMenuProps = {
  menuText: string
  options: NavMenuOption[]
}

export const SubMenu: React.FC<SubMenuProps> = ({
  menuText,
  options,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const menuRef = useRef<HTMLLIElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const closeMenu = useCallback((): void => {
    setIsOpen(false)
    buttonRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, closeMenu])

  return (
    <DesktopNavItem ref={menuRef}>
      <SiteNavButton
        ref={buttonRef}
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {menuText}
        <DropdownChevron open={isOpen} aria-hidden="true" />
      </SiteNavButton>
      {isOpen && (
        <SubDropdown role="menu">
          {options.map((o) => (
            <li key={o.linkText} role="none">
              <Lk link={o.link} alt={o.linkText} role="menuitem" onClick={closeMenu}>{o.linkText}</Lk>
            </li>
          ))}
        </SubDropdown>
      )}
    </DesktopNavItem>
  )
}
